import PaymentType from 'app/src/data/models/PaymentType';
import PaymentCollection from 'app/src/data/models/PaymentCollection';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import _ from 'underscore';

export default class PaymentList {
  /**
   * Creates a new PaymentList from the supplied SalePayment and Payment collections.
   *
   * @param {SalePaymentCollection} salePayments  The collection of sale payments
   * @param {Payment} servicePayments     The collection of service payments
   */
  static fromPayments(
    salePayments = new SalePaymentCollection(),
    servicePayments = new PaymentCollection()
  ) {
    let payments = [];
    salePayments.forEach(salePayment => {
      let payment = servicePayments.findWhere(
        servicePayment => servicePayment.id === salePayment.get('paymentID')
      );

      payments.push(PaymentFactory.createPaymentFromModel(salePayment, payment));
    });

    return new PaymentList(payments);
  }

  /**
   * The constructor
   *
   * @param {Array<Object>} payments    An array of payment objects with which to initialize the list
   */
  constructor(payments = []) {
    this._payments = payments;
  }

  /**
   * Retrieves the number of payments in the list.
   */
  get length() {
    return this._payments.length;
  }

  /**
   * Retrieves the payment at the index specified in the list.
   *
   * @param {Number} index The index of the payment
   *
   * @return {Object}     The payment object at the index specified
   */
  atIndex(index) {
    return this._payments[index];
  }

  /**
   * Adds a payment to the list
   *
   * @param {Object} payment  The payment to add
   */
  add(payment) {
    this._payments.push(payment);
  }

  /**
   * Removes the payment at the index specified from the list
   *
   * @param {Number} index    The index of the payment to remove
   */
  removeAtIndex(index) {
    this._payments.splice(index, 1);
  }

  /**
   * Removes the first payment with all the matching properties either in the sale payment or the service payment
   * model for the payment.
   *
   * @param {Object} properties   The properties to match against the sale and service payment models
   */
  removePaymentWhere(properties) {
    const paymentIndex = this.findIndex(
      payment =>
        _.isMatch(payment.model.attributes, properties) ||
        _.isMatch(payment.serviceModel.attributes, properties)
    );
    if (paymentIndex >= 0) {
      this.removeAtIndex(paymentIndex);
    }
  }

  /**
   * Empties the payment list.
   */
  clear() {
    this._payments = [];
  }

  /**
   * Retrieves a new list of the payments of the specified type.
   *
   * @param {PaymentType.Status} type     The type of payments in the returned list
   */
  getPaymentsOfType(type) {
    let payments = new PaymentList();

    this.forEach(payment => {
      const paymentType = payment.model.getRelation('PaymentType', PaymentType);
      if (paymentType.get('type') === type) {
        payments.add(payment);
      }
    });

    return payments;
  }

  /**
   * Delegates to the built-in forEach method to execute a function on each payment in the list.
   *
   * @param {arguments} args  The arguments
   */
  forEach(...args) {
    this._payments.forEach(...args);
  }

  /**
   * Delegates to the built-in findIndex method to find the index of element in the list using
   * a truth comparison function for each element in the list.
   *
   * @param {arguments} args  The arguments
   */
  findIndex(...args) {
    return _.findIndex(this._payments, ...args);
  }

  /**
   * Delegates to the built-in find method to locate a payment based on a supplied iterator function.
   *
   * @param {arguments} args The arguments
   */
  find(...args) {
    return _.find(this._payments, ...args);
  }

  /**
   * Delegates to the built-in filter method to locate a payment based on a supplied iterator function.
   *
   * @param {arguments} args The arguments
   */
  filter(...args) {
    return new PaymentList(this._payments.filter(...args));
  }

  /**
   * Finds a payment in the list containing all the properties specified in either the sale or
   * service payment model.
   *
   * @param {Object} properties   The properties to match against either the sale or service payment model
   */
  findWhere(properties) {
    return this.find(
      payment =>
        _.isMatch(payment.model.attributes, properties) ||
        _.isMatch(payment.serviceModel.attributes, properties)
    );
  }

  /**
   * Returns a SalePaymentCollection representation of the payment list.
   *
   * @return {SalePaymentCollection}  The list represented as a SalePaymentCollection
   */
  toSalePaymentCollection() {
    let salePayments = new SalePaymentCollection();

    this.forEach(payment => {
      salePayments.add(payment.model);
    });

    return salePayments;
  }

  /**
   * Returns a PaymentCollection representation of the payment list.
   *
   * @return {PaymentCollection}  The list represented as a PaymentCollection
   */
  toPaymentCollection() {
    let servicePayments = new PaymentCollection();

    this.forEach(payment => {
      servicePayments.add(payment.serviceModel);
    });

    return servicePayments;
  }

  /**
   * Returns the JSON representation of the payment list.
   */
  toJSON() {
    return JSON.parse(JSON.stringify(this._payments));
  }
}
